import React, { useEffect, useState, useCallback } from 'react';
import { priceBenchmarkApi, documentsApi, opportunitiesApi } from '../api/client';
import { markOnboardingStep } from '../lib/onboarding';

/* ============================================================================
 * Price Benchmark — Acquisition sub-tab (Phase 1)
 * Answers "what have agencies paid for services like this?" by reading the
 * precomputed price-benchmark rollup (fti-simple-price-benchmark) for the
 * opportunity's own NAICS and PSC. Auto-scoped: no input required.
 *
 * UX principles baked in:
 *  - Always show sample size (N) and the covered year range next to numbers,
 *    so a single figure never implies false precision.
 *  - Lead with the DISTRIBUTION (p10..p90 with median), not a lone average —
 *    federal award dollars are heavily right-skewed (a few mega-awards pull the
 *    mean far above the median).
 *  - The percentiles/min/mean/max are ALL-YEARS (precomputed). The year filter
 *    only trims the yearly bar chart; this is labeled explicitly.
 * ==========================================================================*/

interface Props {
  opp: any;
  oppId: string;
}

export interface Block {
  found: boolean;
  code: string;
  kind: string;
  label: string;
  count: number;
  priced_count: number;
  total_dollars: number;
  min: number;
  max: number;
  mean: number;
  p10: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  top_agencies: { name: string; count: number; dollars: number }[];
  by_year: Record<string, { count: number; dollars: number }>;
  year_min: string | null;
  year_max: string | null;
  built_at?: number;
}

const money = (n: number): string => {
  if (n == null || isNaN(n)) return '$0';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + Math.round(n).toLocaleString();
};

const moneyFull = (n: number): string =>
  '$' + Math.round(n || 0).toLocaleString();

/** In-app link to the award record in our SAM Library. Prefers the exact
 * record id (?award=<id>, opens that record directly); falls back to a PIID
 * search when the id isn't available. Opens in a new browser tab. */
const awardRecordUrl = (r: RefAward): string | null => {
  if (r.id) return `/sam-library?award=${encodeURIComponent(r.id)}`;
  if (r.piid) return `/sam-library?search=${encodeURIComponent(r.piid)}`;
  return null;
};

const card: React.CSSProperties = {
  background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16,
};

/** A horizontal percentile band: p10 — p25 — [median] — p75 — p90, scaled to p90. */
function DistributionBar({ b }: { b: Block }) {
  const scaleMax = b.p90 > 0 ? b.p90 : (b.max || 1);
  const pct = (v: number) => Math.max(0, Math.min(100, (v / scaleMax) * 100));
  const p25L = pct(b.p25);
  const p75L = pct(b.p75);
  const medL = pct(b.median);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ position: 'relative', height: 44 }}>
        {/* full range track p10..p90 */}
        <div style={{
          position: 'absolute', top: 18, left: `${pct(b.p10)}%`, right: `${100 - pct(b.p90)}%`,
          height: 8, background: '#e0e7ff', borderRadius: 4,
        }} />
        {/* interquartile box p25..p75 */}
        <div style={{
          position: 'absolute', top: 15, left: `${p25L}%`, width: `${Math.max(1, p75L - p25L)}%`,
          height: 14, background: '#c7d2fe', borderRadius: 4,
        }} />
        {/* median marker */}
        <div style={{
          position: 'absolute', top: 10, left: `${medL}%`, width: 3, height: 24,
          background: '#4338ca', transform: 'translateX(-1px)',
        }} title={`Median ${moneyFull(b.median)}`} />
        {/* labels */}
        <div style={{ position: 'absolute', top: 30, left: `${p25L}%`, fontSize: 10, color: '#6b7280', transform: 'translateX(-50%)' }}>{money(b.p25)}</div>
        <div style={{ position: 'absolute', top: 0, left: `${medL}%`, fontSize: 10, color: '#4338ca', fontWeight: 700, transform: 'translateX(-50%)' }}>med {money(b.median)}</div>
        <div style={{ position: 'absolute', top: 30, left: `${p75L}%`, fontSize: 10, color: '#6b7280', transform: 'translateX(-50%)' }}>{money(b.p75)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
        <span>p10 {money(b.p10)}</span>
        <span>p90 {money(b.p90)}</span>
      </div>
    </div>
  );
}

/** Yearly award-count + dollars bar chart (honors the year filter). */
function YearChart({ byYear }: { byYear: Record<string, { count: number; dollars: number }> }) {
  const years = Object.keys(byYear).sort();
  if (!years.length) return null;
  const maxDollars = Math.max(...years.map(y => byYear[y].dollars || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, marginTop: 8 }}>
      {years.map(y => {
        const d = byYear[y];
        const h = Math.max(2, (d.dollars / maxDollars) * 100);
        return (
          <div key={y} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 22 }}
               title={`${y}: ${d.count} awards, ${moneyFull(d.dollars)} obligated`}>
            <div style={{ fontSize: 9, color: '#6b7280', marginBottom: 2 }}>{d.count}</div>
            <div style={{ width: '100%', height: `${h}%`, background: '#818cf8', borderRadius: '3px 3px 0 0' }} />
            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{y.slice(2)}</div>
          </div>
        );
      })}
    </div>
  );
}

export function BlockView({ b }: { b: Block }) {
  const kindLabel = b.kind === 'naics' ? 'NAICS' : 'PSC';
  if (!b.found) {
    return (
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{kindLabel} {b.code}</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
          No award history found for this {kindLabel} code in the library.
        </div>
      </div>
    );
  }
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{kindLabel} {b.code}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{b.label}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Sample</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#4338ca' }}>
            N = {b.priced_count.toLocaleString()}
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>
            {b.year_min && b.year_max ? `${b.year_min}–${b.year_max}` : ''}
          </div>
        </div>
      </div>

      {/* Distribution (all-years) */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
          Obligation per award — distribution (all years)
        </div>
        <DistributionBar b={b} />
      </div>

      {/* Headline stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 16 }}>
        {[
          { k: 'Median', v: moneyFull(b.median), hi: true, note: 'typical' },
          { k: 'Mean', v: moneyFull(b.mean), note: 'skewed by large awards' },
          { k: 'Low (p10)', v: moneyFull(b.p10) },
          { k: 'High (p90)', v: moneyFull(b.p90) },
        ].map(s => (
          <div key={s.k} style={{ background: s.hi ? '#eef2ff' : '#f9fafb', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>{s.k}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: s.hi ? '#4338ca' : '#374151' }}>{s.v}</div>
            {s.note && <div style={{ fontSize: 9, color: '#9ca3af' }}>{s.note}</div>}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>
        Min {moneyFull(b.min)} · Max {moneyFull(b.max)} · Total obligated {money(b.total_dollars)} across {b.priced_count.toLocaleString()} priced awards ({b.count.toLocaleString()} total records).
      </div>

      {/* Top agencies */}
      {b.top_agencies?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
            Top buying agencies
          </div>
          {b.top_agencies.slice(0, 5).map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
              <span style={{ color: '#374151' }}>{a.name}</span>
              <span style={{ color: '#6b7280' }}>{a.count.toLocaleString()} awards · {money(a.dollars)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Year distribution */}
      {Object.keys(b.by_year || {}).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
            Awards by year (count above bar; height = dollars)
          </div>
          <YearChart byYear={b.by_year} />
        </div>
      )}
    </div>
  );
}

interface RefAward {
  id?: string; band?: string; piid?: string; awardee?: string; agency?: string;
  dollars?: number; date?: string; naics?: string; psc?: string; description?: string;
}

interface Estimate {
  low: number | null;
  likely: number | null;
  high: number | null;
  unit: string;
  procurement_type?: string | null;
  confidence: string;
  rationale: string;
  drivers: string[];
  unit_breakdown?: {
    quantity?: number | null;
    unit_label?: string;
    per_unit?: number | null;
    per_unit_basis?: string | null;
    term?: string | null;
  } | null;
  basis: {
    naics?: string | null; psc?: string | null;
    anchor_n?: number; anchor_median?: number;
    anchor_p25?: number; anchor_p75?: number;
    anchor_years?: (string | null)[];
  };
  reference_awards: RefAward[];
  reference_sets?: { kind: string; code: string; label?: string; n?: number; median?: number; awards: RefAward[] }[];
  model?: string;
}

const confColor: Record<string, string> = { high: '#059669', medium: '#d97706', low: '#dc2626' };

/** Assemble a work-description draft from the opportunity's own text + AI
 * analysis + typed intake + uploaded document content. Used to pre-fill the
 * estimator in the Intake context so the CO doesn't retype what's in the system. */
async function buildDescriptionFromOpportunity(opp: any, oppId: string): Promise<string> {
  const parts: string[] = [];
  const push = (label: string, v: any) => {
    if (v == null) return;
    const s = Array.isArray(v) ? v.filter(Boolean).join('; ') : String(v);
    if (s.trim()) parts.push(`${label}: ${s.trim()}`);
  };

  push('Title', opp?.title);
  push('Description', opp?.full_description || opp?.description);

  // Parsed AI analysis (scope, executive summary, what the government seeks).
  try {
    const a = opp?.analysis ? (typeof opp.analysis === 'string' ? JSON.parse(opp.analysis) : opp.analysis) : null;
    if (a) {
      push('Scope', a.scope);
      push('Executive summary', a.executive_summary);
      push('What the government seeks', (a.what_government_seeks || []).slice(0, 12));
      push('Key capabilities', (a.detailed_capabilities || []).slice(0, 15));
    }
  } catch { /* ignore malformed analysis */ }

  // Typed intake narrative.
  try {
    const d = opp?.intake_data ? (typeof opp.intake_data === 'string' ? JSON.parse(opp.intake_data) : opp.intake_data) : null;
    if (d) {
      push('Mission need', d.mission_need);
      push('Intended outcome', d.intended_outcome);
      push('Functions to be performed', d.functions_performed);
      push('Operating environment', d.operating_environment);
    }
  } catch { /* ignore */ }

  // Uploaded + AI-generated document text (bounded server-side).
  if (oppId) {
    try {
      const ctx = await documentsApi.context(oppId, 12000);
      if (ctx?.context && ctx.context.trim()) {
        parts.push(`Source documents:\n${ctx.context.trim().slice(0, 12000)}`);
      }
    } catch { /* documents optional */ }
  }

  return parts.join('\n');
}

interface CostEstimatorProps {
  naics: string;
  psc: string;
  /** When provided (Intake context), the estimator can auto-fill its description
   * from the opportunity's text, analysis, intake and uploaded documents. */
  opp?: any;
  oppId?: string;
}

/** AI cost estimator: describe the work -> grounded range + rationale + real reference awards. */
export function CostEstimator({ naics, psc, opp, oppId }: CostEstimatorProps) {
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [est, setEst] = useState<Estimate | null>(null);
  const canAutoSource = !!(opp || oppId);

  const autoFill = async () => {
    setSourcing(true); setErr('');
    try {
      const draft = await buildDescriptionFromOpportunity(opp, oppId || '');
      if (draft.trim().length < 10) {
        setErr('Not enough opportunity text or documents to auto-fill. Type a description instead.');
      } else {
        setDesc(draft);
      }
    } catch (e: any) {
      setErr(e?.message || 'Could not pull opportunity text');
    } finally {
      setSourcing(false);
    }
  };

  const [savedIgce, setSavedIgce] = useState<boolean>(!!(opp?.igce));
  const [savingIgce, setSavingIgce] = useState(false);

  const run = async () => {
    if (desc.trim().length < 10) { setErr('Describe the work, or click "Use opportunity text & files" to auto-fill.'); return; }
    setBusy(true); setErr(''); setNote(''); setEst(null); setSavedIgce(false);
    try {
      const res = await priceBenchmarkApi.estimate({ description: desc.trim(), naics, psc });
      if (res.estimate) { setEst(res.estimate); markOnboardingStep('run_estimate'); }
      else setNote(res.note || 'No estimate could be produced.');
    } catch (e: any) {
      setErr(e?.message || 'Estimate failed');
    } finally {
      setBusy(false);
    }
  };

  // Persist the current estimate as this opportunity's IGCE. Also mirrors the
  // 'likely' figure into estimated_value so the rest of the app sees it.
  const saveAsIgce = async () => {
    if (!est || !oppId) return;
    setSavingIgce(true); setErr('');
    try {
      const igce = {
        description: desc.trim(),
        naics, psc,
        low: est.low, likely: est.likely, high: est.high,
        unit: est.unit,
        procurement_type: est.procurement_type || null,
        confidence: est.confidence,
        rationale: est.rationale,
        drivers: est.drivers || [],
        unit_breakdown: est.unit_breakdown || null,
        basis: est.basis || null,
        source: 'ai_cost_estimator',
        model: est.model || null,
        saved_at: Math.floor(Date.now() / 1000),
      };
      await opportunitiesApi.update(oppId, {
        igce,
        estimated_value: est.likely != null ? String(Math.round(est.likely)) : '',
      });
      setSavedIgce(true);
    } catch (e: any) {
      setErr(e?.message || 'Could not save IGCE');
    } finally {
      setSavingIgce(false);
    }
  };

  return (
    <div style={{ ...card, borderColor: '#c7d2fe', background: '#fbfbff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>🧮</span>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#3730a3', margin: 0 }}>AI Cost Estimate</h4>
      </div>
      <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 10px' }}>
        Describe the work you're buying (scope, duration, labor mix, clearance, place of performance, quantity).
        The estimate is grounded in the historical award benchmark for {naics ? `NAICS ${naics}` : ''}{naics && psc ? ' / ' : ''}{psc ? `PSC ${psc}` : ''} and cites the real awards behind it.
      </p>
      {canAutoSource && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={autoFill} disabled={sourcing}
                  style={{ padding: '6px 12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: sourcing ? 'default' : 'pointer' }}>
            {sourcing ? 'Pulling opportunity text…' : '↧ Use opportunity text & files'}
          </button>
          <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 8 }}>
            Fills from this opportunity's title, description, AI analysis, intake, and uploaded documents. Edit before estimating.
          </span>
        </div>
      )}
      <textarea
        value={desc}
        onChange={e => setDesc(e.target.value)}
        placeholder="e.g. Cloud migration support, 3 senior engineers with Secret clearance, on-site in DC, 12-month base + 1 option year."
        rows={3}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>Advisory only — not an official IGCE. Always verify against current market conditions.</span>
        <button onClick={run} disabled={busy}
                style={{ padding: '8px 18px', background: busy ? '#a5b4fc' : '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Estimating…' : 'Estimate cost'}
        </button>
      </div>

      {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{err}</div>}
      {note && <div style={{ fontSize: 12, color: '#92400e', marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>{note}</div>}

      {est && (
        <div style={{ marginTop: 14 }}>
          {/* Range */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>Low</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>{est.low != null ? moneyFull(est.low) : '—'}</div>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', padding: '0 20px' }}>
              <div style={{ fontSize: 10, color: '#4338ca', textTransform: 'uppercase', fontWeight: 700 }}>Likely</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#4338ca' }}>{est.likely != null ? moneyFull(est.likely) : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>High</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>{est.high != null ? moneyFull(est.high) : '—'}</div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>{est.unit}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: confColor[est.confidence] || '#6b7280' }}>
                {est.confidence} confidence
              </div>
              {est.procurement_type && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                  {est.procurement_type.replace(/_/g, ' ')}
                </div>
              )}
            </div>
          </div>

          {/* Unit breakdown (SaaS seats, licenses, hardware qty, etc.) */}
          {est.unit_breakdown && (est.unit_breakdown.quantity || est.unit_breakdown.per_unit) && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, color: '#166534' }}>
              <strong>Unit breakdown: </strong>
              {est.unit_breakdown.per_unit != null ? `≈ ${moneyFull(est.unit_breakdown.per_unit)}` : ''}
              {est.unit_breakdown.per_unit != null && est.unit_breakdown.per_unit_basis ? ` ${est.unit_breakdown.per_unit_basis}` : ''}
              {est.unit_breakdown.per_unit != null ? ` per ${est.unit_breakdown.unit_label || 'unit'}` : ''}
              {est.unit_breakdown.quantity != null ? `${est.unit_breakdown.per_unit != null ? ' · ' : ''}${est.unit_breakdown.quantity.toLocaleString()} ${est.unit_breakdown.unit_label || 'unit'}${est.unit_breakdown.quantity === 1 ? '' : 's'}` : ''}
              {est.unit_breakdown.term ? ` · ${est.unit_breakdown.term}` : ''}
            </div>
          )}

          {/* Save as IGCE — only in an opportunity context */}
          {oppId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <button onClick={saveAsIgce} disabled={savingIgce}
                      style={{ padding: '7px 16px', background: savedIgce ? '#dcfce7' : '#059669', color: savedIgce ? '#166534' : 'white', border: savedIgce ? '1px solid #86efac' : 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: savingIgce ? 'default' : 'pointer' }}>
                {savingIgce ? 'Saving…' : savedIgce ? '✓ Saved as IGCE' : 'Save as IGCE'}
              </button>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                Records this estimate as the opportunity's Independent Government Cost Estimate (sets estimated value). You can re-run and re-save anytime.
              </span>
            </div>
          )}

          {/* Rationale */}
          {est.rationale && (
            <p style={{ fontSize: 13, color: '#374151', marginTop: 12, lineHeight: 1.5 }}>{est.rationale}</p>
          )}

          {/* Drivers */}
          {est.drivers?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {est.drivers.map((d, i) => (
                <span key={i} style={{ fontSize: 11, background: '#eef2ff', color: '#4338ca', borderRadius: 9999, padding: '3px 10px' }}>{d}</span>
              ))}
            </div>
          )}

          {/* Basis */}
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 10 }}>
            Grounded on {est.basis?.anchor_n?.toLocaleString?.() || '—'} awards
            {est.basis?.anchor_years?.[0] ? ` (${est.basis.anchor_years[0]}–${est.basis.anchor_years[1]})` : ''}
            {est.basis?.anchor_median != null ? ` · benchmark median ${moneyFull(est.basis.anchor_median)}` : ''}
            {est.basis?.anchor_p25 != null && est.basis?.anchor_p75 != null ? ` · p25–p75 ${money(est.basis.anchor_p25)}–${money(est.basis.anchor_p75)}` : ''}
            {est.model ? ` · ${est.model}` : ''}
          </div>

          {/* Reference awards — one labeled set per code (NAICS = industry-wide,
              PSC = work-specific). Falls back to the flat list if sets absent. */}
          {(() => {
            const sets = (est.reference_sets && est.reference_sets.length)
              ? est.reference_sets
              : (est.reference_awards?.length
                  ? [{ kind: 'naics', code: est.basis?.naics || '', awards: est.reference_awards } as any]
                  : []);
            if (!sets.length) return null;
            return (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
                  Reference awards (real contracts behind this estimate)
                </div>
                {sets.map((set, si) => (
                  <div key={si} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4338ca', marginBottom: 4 }}>
                      {set.kind === 'naics' ? 'NAICS' : 'PSC'} {set.code}
                      {set.label ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {set.label}</span> : null}
                      {set.n != null ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {set.n.toLocaleString()} awards</span> : null}
                      <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                        {' '}· {set.kind === 'naics' ? 'industry-wide' : 'work-specific'}
                      </span>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                      {set.awards.map((r: RefAward, i: number) => {
                        const url = awardRecordUrl(r);
                        return (
                        <div key={i} style={{ padding: '8px 10px', borderBottom: i < set.awards.length - 1 ? '1px solid #f3f4f6' : 'none', background: i % 2 ? '#fafafa' : 'white' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                              {r.awardee || '—'} <span style={{ color: '#9ca3af', fontWeight: 400 }}>· {r.agency || ''}</span>
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca' }}>
                              {r.dollars != null ? moneyFull(Number(r.dollars)) : '—'}
                              {r.band ? <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 500 }}> ({r.band})</span> : null}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                            {r.piid ? (
                              url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer"
                                   title="Open this award in the SAM Library (new tab)"
                                   style={{ fontFamily: 'monospace', color: '#4338ca', textDecoration: 'none' }}>
                                  {r.piid} ↗
                                </a>
                              ) : <span style={{ fontFamily: 'monospace' }}>{r.piid}</span>
                            ) : null}
                            {r.date ? ` · ${r.date}` : ''}
                            {r.naics ? ` · NAICS ${r.naics}` : ''}
                            {r.psc ? ` · PSC ${r.psc}` : ''}
                            {r.description ? ` · ${r.description}` : ''}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default function PriceBenchmarkTab({ opp, oppId }: Props) {
  const naics = (opp?.naics_code || '').trim();
  const psc = (opp?.psc_code || opp?.product_service_code || '').trim();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [naicsBlock, setNaicsBlock] = useState<Block | null>(null);
  const [pscBlock, setPscBlock] = useState<Block | null>(null);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');

  const load = useCallback(async () => {
    if (!naics && !psc) return;
    setLoading(true);
    setError('');
    try {
      const res = await priceBenchmarkApi.get({ naics, psc, from: yearFrom, to: yearTo });
      setNaicsBlock(res.naics || null);
      setPscBlock(res.psc || null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load price benchmark');
    } finally {
      setLoading(false);
    }
  }, [naics, psc, yearFrom, yearTo]);

  useEffect(() => { load(); }, [load]);

  if (!naics && !psc) {
    return (
      <div style={card}>
        <h3 style={{ fontSize: 14, margin: 0, color: '#374151' }}>Price Benchmark</h3>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
          This opportunity has no NAICS or PSC code yet. Add a NAICS or PSC code on the
          Overview tab to see what agencies have paid for similar work.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, margin: 0, color: '#1f2937' }}>Price Benchmark</h3>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>
            What federal agencies have paid for work in this opportunity's{naics ? ` NAICS ${naics}` : ''}{naics && psc ? ' and' : ''}{psc ? ` PSC ${psc}` : ''}. Use to sanity-check your independent cost estimate.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>Years</span>
          <input value={yearFrom} onChange={e => setYearFrom(e.target.value.replace(/\D/g, '').slice(0, 4))}
                 placeholder="from" style={{ width: 56, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }} />
          <span style={{ color: '#9ca3af' }}>–</span>
          <input value={yearTo} onChange={e => setYearTo(e.target.value.replace(/\D/g, '').slice(0, 4))}
                 placeholder="to" style={{ width: 56, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }} />
          <button onClick={load} style={{ padding: '5px 12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
        </div>
      </div>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', fontSize: 11, color: '#92400e', marginBottom: 16 }}>
        Figures are historical prime-award obligations from federal award data, grouped by code. The distribution (median / percentiles) is computed across all years; the year filter trims only the yearly chart. Award dollars vary widely by scope, so treat these as directional benchmarks, not fixed prices.
      </div>

      {loading && <div style={{ fontSize: 13, color: '#6b7280', padding: 20 }}>Loading benchmark…</div>}
      {error && <div style={{ fontSize: 13, color: '#dc2626', padding: 12 }}>{error}</div>}

      {!loading && !error && (
        <>
          {naics && naicsBlock && <BlockView b={naicsBlock} />}
          {psc && pscBlock && <BlockView b={pscBlock} />}
        </>
      )}

      {/* AI cost estimator — describe the work, get a grounded range + reference awards.
          Passing opp/oppId enables auto-fill from the opportunity's text + documents. */}
      <CostEstimator naics={naics} psc={psc} opp={opp} oppId={oppId || opp?.id} />
    </div>
  );
}
